import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Minus, Plus, ArrowRight, ShieldCheck, ChevronDown, CheckCircle2, Shield, BookOpen, FileCheck, Users, Activity } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mengenrabatt: ab 10/20/50 Mitarbeitern
const calcPricing = (count, cycle) => {
  const monthlyPerSeat = 10.90;
  const yearlyPerSeat = 109.00; // entspricht ~17 % Rabatt
  const annualBase = cycle === 'yearly' ? yearlyPerSeat * count : monthlyPerSeat * 12 * count;
  let discount = 0;
  if (count >= 50) discount = 0.20;
  else if (count >= 20) discount = 0.15;
  else if (count >= 10) discount = 0.10;
  const annual = annualBase * (1 - discount);
  return {
    discount: Math.round(discount * 100),
    annual: annual.toFixed(2),
    monthly: (annual / 12).toFixed(2),
    perSeatMonthly: (annual / 12 / count).toFixed(2),
    raw: annual };
};

/**
 * License-Konfigurator als Modal.
 * Wird geöffnet, wenn ein Nutzer auf der LandingPage einen Kauf-/Test-CTA klickt.
 * Übergibt die Mitarbeiter-Anzahl als `quantity` an Digistore24 bzw. als URL-Param
 * an die Registrierung.
 */
export const LicenseConfiguratorModal = ({ open, onOpenChange, mode = 'buy' }) => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState(1);
  const [cycle, setCycle] = useState('yearly');
  const [includesOpen, setIncludesOpen] = useState(false);
  const [todayCount, setTodayCount] = useState(null);

  // Social-Proof beim Öffnen des Modals laden
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/learning/social-proof/today`);
        const data = await res.json();
        if (!cancelled && data?.success && typeof data.today_active_count === 'number') {
          setTodayCount(data.today_active_count);
        }
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const pricing = useMemo(() => calcPricing(employees, cycle), [employees, cycle]);

  // WICHTIG: Niemals direkt zu Digistore24!
  // Beide Pfade (Test & Kauf) führen IMMER zuerst zur Firmendaten-Eingabe.
  // Nach erfolgreicher Registrierung leitet die RegisterPage mit der Quantity
  // dann an Digistore24 weiter.
  const goToDataEntry = () => {
    onOpenChange(false);
    const params = new URLSearchParams({
      employees: String(employees),
      cycle });
    if (mode === 'buy') params.set('autocheckout', '1');
    navigate(`/register?${params.toString()}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-gray-950 border border-yellow-600/30 max-w-md p-0 overflow-hidden"
        data-testid="license-configurator-modal"
      >
        <div className="p-6 sm:p-7">
          <DialogHeader>
            <DialogTitle className="text-white text-xl sm:text-2xl">
              Für wie viele Mitarbeiter möchten Sie den Zugang einrichten?
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-400 text-sm mt-1">
            Wählen Sie die Anzahl der Lizenzen – der Preis aktualisiert sich automatisch.
          </p>

          {/* Counter */}
          <div className="mt-5">
            <label className="text-gray-400 text-xs mb-2 block text-center">Anzahl Mitarbeiter</label>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setEmployees((c) => Math.max(1, c - 1))}
                className="w-11 h-11 bg-gray-900 border border-gray-800 rounded-full flex items-center justify-center text-white hover:bg-gray-800"
                data-testid="lc-decrement"
                aria-label="Weniger"
              >
                <Minus className="w-4 h-4" />
              </button>
              <input
                type="number"
                min={1}
                value={employees}
                onChange={(e) => setEmployees(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-24 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-white text-center text-3xl font-bold"
                data-testid="lc-employee-input"
              />
              <button
                onClick={() => setEmployees((c) => c + 1)}
                className="w-11 h-11 bg-gray-900 border border-gray-800 rounded-full flex items-center justify-center text-white hover:bg-gray-800"
                data-testid="lc-increment"
                aria-label="Mehr"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Billing cycle */}
          <div className="mt-5">
            <div className="flex rounded-lg overflow-hidden border border-gray-800">
              <button
                onClick={() => setCycle('monthly')}
                className={`flex-1 py-2.5 text-sm font-medium ${cycle === 'monthly' ? 'bg-yellow-500 text-black' : 'bg-gray-900 text-gray-400'}`}
                data-testid="lc-cycle-monthly"
              >
                Monatlich
              </button>
              <button
                onClick={() => setCycle('yearly')}
                className={`flex-1 py-2.5 text-sm font-medium ${cycle === 'yearly' ? 'bg-yellow-500 text-black' : 'bg-gray-900 text-gray-400'}`}
                data-testid="lc-cycle-yearly"
              >
                Jährlich <span className="text-[10px] opacity-80">(17 % sparen)</span>
              </button>
            </div>
          </div>

          {/* Price card */}
          <div className="mt-5 bg-gray-900 border border-gray-800 rounded-xl p-5 text-center">
            {pricing.discount > 0 && (
              <div className="inline-block bg-green-500/15 text-green-400 text-xs px-3 py-1 rounded-full mb-2">
                {pricing.discount} % Mengenrabatt
              </div>
            )}
            <div className="text-3xl font-bold text-yellow-500" data-testid="lc-price-monthly">
              {pricing.monthly}€<span className="text-base text-gray-400 font-normal">/Monat</span>
            </div>
            <div className="text-gray-400 text-sm mt-1">
              ≈ {pricing.perSeatMonthly}€ pro Mitarbeiter/Monat
            </div>
            {cycle === 'yearly' && (
              <div className="text-gray-500 text-xs mt-1">Jahresbetrag: {pricing.annual}€</div>
            )}
          </div>

          {/* Live Social Proof */}
          {typeof todayCount === 'number' && todayCount > 0 && (
            <div
              className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-300 bg-green-500/8 border border-green-500/25 rounded-lg px-3 py-2"
              data-testid="lc-social-proof"
            >
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
              </span>
              <Activity className="w-3.5 h-3.5 text-green-400" />
              <span>
                Heute haben sich <strong className="text-white">{todayCount}</strong> {todayCount === 1 ? 'Elektrobetrieb' : 'Elektrobetriebe'} für ein Paket entschieden
              </span>
            </div>
          )}

          {/* Was ist enthalten? - Akkordeon */}
          <button
            onClick={() => setIncludesOpen((o) => !o)}
            className="mt-5 w-full flex items-center justify-between bg-gray-900/70 hover:bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 transition"
            data-testid="lc-includes-toggle"
            aria-expanded={includesOpen}
          >
            <span className="text-white font-medium text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" /> Was ist enthalten?
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${includesOpen ? 'rotate-180' : ''}`} />
          </button>
          {includesOpen && (
            <div className="mt-2 bg-gray-900/40 border border-gray-800 rounded-xl p-4 space-y-3" data-testid="lc-includes-content">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center shrink-0">
                  <Shield className="w-4 h-4 text-green-500" />
                </div>
                <div className="text-sm">
                  <p className="text-white font-medium leading-tight">DGUV- & BG-konform</p>
                  <p className="text-gray-400 text-xs mt-0.5">Rechtssicher: alle Pflicht-Unterweisungen für Elektrobetriebe abgedeckt.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-yellow-500/15 flex items-center justify-center shrink-0">
                  <BookOpen className="w-4 h-4 text-yellow-500" />
                </div>
                <div className="text-sm">
                  <p className="text-white font-medium leading-tight">16 Pflichtschulungen inklusive</p>
                  <p className="text-gray-400 text-xs mt-0.5">DGUV V1, VDE 0105-100, Leitern & Gerüste, PV, Smart Home, KI – uvm.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center shrink-0">
                  <FileCheck className="w-4 h-4 text-blue-400" />
                </div>
                <div className="text-sm">
                  <p className="text-white font-medium leading-tight">Auto-Zertifikate per E-Mail</p>
                  <p className="text-gray-400 text-xs mt-0.5">Sofort nach bestandenem Quiz – revisionssicher archiviert.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-fuchsia-500/15 flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 text-fuchsia-400" />
                </div>
                <div className="text-sm">
                  <p className="text-white font-medium leading-tight">Chef-Cockpit mit Schulungsmatrix</p>
                  <p className="text-gray-400 text-xs mt-0.5">Alle Mitarbeiter-Stände, Fortschritte & Nachweise auf einen Blick.</p>
                </div>
              </div>
            </div>
          )}

          {/* Single CTA: führt IMMER zur Firmendaten-Eingabe, NIE direkt zu Digistore24 */}
          <div className="mt-5">
            <Button
              onClick={goToDataEntry}
              className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold py-5 text-base"
              data-testid="lc-next-btn"
            >
              Weiter zur Dateneingabe & Kasse <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-gray-500 text-[11px] text-center mt-2">
              {mode === 'trial'
                ? '14 Tage testen – heute 0,00 €, Abbuchung erst nach der Testphase.'
                : 'Nächster Schritt: Firmendaten eingeben → sicher zur Kasse.'}
            </p>
          </div>

          <p className="text-gray-500 text-xs text-center mt-4 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-green-500" /> Lizenzen jederzeit im Dashboard flexibel zuweisbar &middot; Jederzeit kündbar
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LicenseConfiguratorModal;
